import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { DropResult } from "react-beautiful-dnd";
import OutsideClickHandler from "react-outside-click-handler";
import { graphql } from "react-relay";

import { faArchive, faCopy, faEllipsisV, faLink, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faBullseyeArrow,
	faCheck,
	faExclamationCircle,
	faLock,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import copy from "copy-to-clipboard";
import { omit } from "lodash";
import type { Moment } from "moment";
import type { RecordSourceSelectorProxy } from "relay-runtime";

import Alert from "~/common/Alert";
import AppContext from "~/common/AppContext";
import type { AssigneeType, TeamType, UserType } from "~/common/AssigneeFilter";
import ViewportContext from "~/common/ViewportContext";
import { confirm } from "~/lib/prompts";
import { commitMutationPromise, RunQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import EditableKeyResultList from "~/okrs/components/EditableKeyResultList";
import type { ObjectiveType as ParentObjectiveType } from "~/okrs/components/ObjectiveListInput";
import Settings from "~/okrs/components/Settings";
import { MetricProgresses, MetricTypes } from "~/okrs/components/TargetInput";
import CreateObjective from "~/okrs/CreateObjective";
import { keyResultCreateOrEditMutation, keyResultDeleteMutation, keyResultReorderMutation } from "~/okrs/KeyResult";
import Objective, {
	type ObjectiveAttributes,
	objectiveCreateOrEditMutation,
	type ObjectiveType,
	Visibility,
} from "~/okrs/Objective";
import { OKRsContext } from "~/okrs/ObjectiveList";
import Updates from "~/okrs/Updates";
import { calculateKeyResultProgress, calculateObjectiveProgress } from "~/okrs/utils";
import { Button, useModals } from "~/ui";
import Popup from "~/ui/legacy/Popup";
import { HelpCenterIcon, IconButton, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import type { SelectorOption } from "~/ui/selectors/Selector";
import toast from "~/ui/toast";

import type { EditObjectiveArchiveMutation } from "~/__generated__/EditObjectiveArchiveMutation.graphql";
import type { EditObjectiveChildrenQuery } from "~/__generated__/EditObjectiveChildrenQuery.graphql";
import type { EditObjectiveDeleteMutation } from "~/__generated__/EditObjectiveDeleteMutation.graphql";
import type { KeyResultCreateOrEditMutation } from "~/__generated__/KeyResultCreateOrEditMutation.graphql";
import type { KeyResultDeleteMutation } from "~/__generated__/KeyResultDeleteMutation.graphql";
import type { KeyResultReorderMutation } from "~/__generated__/KeyResultReorderMutation.graphql";
import type {
	ObjectiveCreateOrEditMutation,
	ObjectiveInput,
} from "~/__generated__/ObjectiveCreateOrEditMutation.graphql";
import type { MetricProgress, MetricType } from "~/__generated__/ObjectiveQuery_Info.graphql";
import type { ObjectiveStatus } from "~/__generated__/ObjectiveQueryQuery.graphql";
import type { teamQuery_Info$data as Team } from "~/__generated__/teamQuery_Info.graphql";

import stylesCreateObjective from "~/okrs/CreateObjective.module.css";

import styles from "./EditObjective.module.css";

enum Alerts {
	MISSING_KEY_RESULTS = "You must set at least one key result",
	READ_ONLY = "This is a public Objective; to make edits, you need to be a contributor.",
	EDIT_ERROR = "An error occurred on objective edit. Please contact support",
	ASSIGNEE_IS_NOT_CONTRIBUTOR = "All the assignees must be editors of the objective.",
}

const AlertIcons = {
	[Alerts.MISSING_KEY_RESULTS]: faExclamationCircle,
	[Alerts.EDIT_ERROR]: faExclamationCircle,
	[Alerts.ASSIGNEE_IS_NOT_CONTRIBUTOR]: faExclamationCircle,
	[Alerts.READ_ONLY]: faLock,
};

const ErrorAlerts = [Alerts.MISSING_KEY_RESULTS, Alerts.EDIT_ERROR, Alerts.ASSIGNEE_IS_NOT_CONTRIBUTOR];

export enum Viewing {
	OVERVIEW,
	UPDATES,
	SETTINGS,
}

export type EditObjectiveProps = {
	objective: ObjectiveAttributes;
	onClose(): void;
	refetch?(id?: string): void;
	fromTeam?: Omit<Team, " $fragmentType">;
	initialView?: Viewing;
};

const archiveObjectiveMutation = graphql`
	mutation EditObjectiveArchiveMutation($objectiveId: ID!) {
		Objective_Archive(objectiveId: $objectiveId) {
			ok
		}
	}
`;

const deleteObjectiveMutation = graphql`
	mutation EditObjectiveDeleteMutation($objectiveId: ID!) {
		Objective_Delete(objectiveId: $objectiveId) {
			ok
		}
	}
`;

const childItemsQuery = graphql`
	query EditObjectiveChildrenQuery($parent: ID!) {
		objectives(parent: $parent, display: LIST) {
			edges {
				node {
					...ObjectiveQuery_Info @relay(mask: false)
				}
			}
		}
	}
`;

export default function EditObjective({
	objective: editObjective,
	onClose,
	refetch,
	fromTeam,
	initialView,
}: EditObjectiveProps) {
	const { user } = useContext(AppContext);
	const { isMobile } = useContext(ViewportContext);
	const [objective, setObjective] = useState<ObjectiveAttributes>(editObjective);
	const [initialSettings, setInitialSettings] = useState<{
		cycle: ObjectiveAttributes["cycle"];
		parent: ObjectiveAttributes["parent"];
		visibility: ObjectiveAttributes["visibility"];
		contributors: ObjectiveAttributes["contributors"];
		contributorsTeams: ObjectiveAttributes["contributorsTeams"];
	}>({
		cycle: objective.cycle,
		parent: objective.parent,
		visibility: objective.visibility,
		contributors: [...objective.contributors],
		contributorsTeams: [...objective.contributorsTeams],
	});
	const [settingsChanged, setSettingsChanged] = useState<boolean>(false);
	const [alert, setAlert] = useState<boolean>(!editObjective.hasAccess);
	const [alertMessage, setAlertMessage] = useState<Alerts | undefined>(
		!editObjective.hasAccess ? Alerts.READ_ONLY : undefined,
	);
	const [view, setView] = useState<Viewing>(initialView || (isMobile ? Viewing.OVERVIEW : Viewing.UPDATES));
	const [isEditLoading, setEditIsLoading] = useState<boolean | null>(null);
	const [isChildQueryLoading, setIsChildQueryLoading] = useState(false);
	const [isLoadingKeyResultCreation, setIsLoadingKeyResultCreation] = useState<boolean>(false);
	const [refetchOnClose, setRefetchOnClose] = useState<boolean>(false);
	const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
	const modals = useModals();

	useEffect(() => {
		if (isMobile) {
			setView(initialView || Viewing.OVERVIEW);
			return;
		}
		setView(initialView || Viewing.UPDATES);
	}, [isMobile, initialView]);

	const onOpenOptions = useCallback(() => {
		setOptionsOpen(true);
	}, []);

	const onCloseOptions = useCallback(() => {
		setOptionsOpen(false);
	}, []);

	const onChildQueryLoading = useCallback(() => {
		setIsChildQueryLoading(true);
	}, []);

	const onChildQueryResponse = useCallback(
		(result: EditObjectiveChildrenQuery["response"]) => {
			setIsChildQueryLoading(false);
			if (result.objectives?.edges) {
				setObjective({ ...objective, children: result.objectives?.edges.map(e => e?.node) as ObjectiveType[] });
			}
		},
		[objective],
	);

	useEffect(() => {
		return () => {
			if (refetchOnClose) {
				refetch?.(editObjective.id);
			}
		};
	}, [refetch, refetchOnClose, editObjective]);

	const onChangeToOverviewView = useCallback(() => {
		setView(Viewing.OVERVIEW);
	}, [setView]);

	const onChangeToUpdatesView = useCallback(() => {
		setView(Viewing.UPDATES);
	}, [setView]);

	const onChangeToSettingsView = useCallback(() => {
		setView(Viewing.SETTINGS);
	}, [setView]);

	const onCopyObjectiveURL = useCallback(() => {
		copy(`${window.location.origin}/objectives/${objective.id}/`);
		toast.success("Copied to clipboard!");
	}, [objective]);

	const onArchiveObjective = useCallback(() => {
		commitMutationPromise<EditObjectiveArchiveMutation>(environment, {
			mutation: archiveObjectiveMutation,
			variables: {
				objectiveId: editObjective.id,
			},
			onCompleted: () => {
				onClose();
				refetch?.(editObjective.id);
			},
		});
	}, [editObjective, onClose, refetch]);

	const onDeleteObjective = useCallback(async () => {
		if (
			await confirm(
				<p>
					Deleting an Objective <b>cannot be undone.</b>
				</p>,
				{
					yesText: "Delete Objective",
					yesProps: { variant: "destructive" },
					title: "Are you sure?",
					simple: true,
				},
			)
		) {
			commitMutationPromise<EditObjectiveDeleteMutation>(environment, {
				mutation: deleteObjectiveMutation,
				variables: {
					objectiveId: editObjective.id,
				},
				onCompleted: () => {
					onClose();
					refetch?.(editObjective.id);
				},
			});
		}
	}, [editObjective, onClose, refetch]);

	const onTitleChange = useCallback(
		(newTitle: string) => {
			setObjective({ ...objective, title: newTitle });
		},
		[objective, setObjective],
	);

	const onDescriptionChange = useCallback(
		(newDescription: string | null) => {
			setObjective({ ...objective, description: newDescription });
		},
		[objective, setObjective],
	);

	const onDueDateChange = useCallback(
		(newDueDate: Moment | null) => {
			setObjective({ ...objective, dueDate: newDueDate ? newDueDate.format("YYYY-MM-DD") : null });
		},
		[objective, setObjective],
	);

	const onStatusChange = useCallback(
		(newStatus: ObjectiveStatus) => {
			setObjective({ ...objective, status: newStatus });
		},
		[objective, setObjective],
	);

	const onAssigneeChange = useCallback(
		(newAssignee: UserType) => {
			const existingAssigneeAsContributor = objective.contributors.find(u => u.id === newAssignee.id);
			const newContributors = existingAssigneeAsContributor
				? objective.contributors
				: [...objective.contributors, newAssignee];
			setObjective({ ...objective, assigneeUser: newAssignee, assigneeTeam: null, contributors: newContributors });
		},
		[objective, setObjective],
	);

	const onAssigneeTeamChange = useCallback(
		(newAssigneeTeam: TeamType) => {
			const existingAssigneeAsContributor = objective.contributorsTeams.find(t => t.id === newAssigneeTeam.id);
			const newContributors = existingAssigneeAsContributor
				? objective.contributorsTeams
				: [...objective.contributorsTeams, newAssigneeTeam];
			setObjective({
				...objective,
				assigneeUser: null,
				assigneeTeam: newAssigneeTeam,
				contributorsTeams: newContributors,
			});
		},
		[objective, setObjective],
	);

	const onCreateKeyResult = useCallback(() => {
		const newKeyResult = {
			id: "",
			title: "",
			assigneeUser: !fromTeam
				? {
						id: user?.id || "",
						email: user?.email || "",
						image: user?.image || "",
						fullName: user?.fullName || "",
						isGuest: !!user?.isGuest,
					}
				: null,
			assigneeTeam: fromTeam
				? {
						id: fromTeam.id,
						title: fromTeam.title,
					}
				: null,
			metricProgress: MetricProgresses.INCREASING,
			metricType: MetricTypes.NUMBER,
			metricCurrency: null,
			metricStart: 0,
			metricCurrent: 0,
			metricTarget: 100,
			dueDate: objective.dueDate ?? null,
			weight: 1,
			progress: 0,
			order: objective.keyResults.length,
		};
		setIsLoadingKeyResultCreation(true);

		commitMutationPromise<KeyResultCreateOrEditMutation>(environment, {
			mutation: keyResultCreateOrEditMutation,
			variables: {
				keyResult: {
					...omit(newKeyResult, ["id", "progress"]),
					assigneeUser: newKeyResult.assigneeUser ? newKeyResult.assigneeUser.id : null,
					assigneeTeam: newKeyResult.assigneeTeam ? newKeyResult.assigneeTeam.id : null,
					objective: objective.id,
				},
			},
			// For new key results we don't currently need to locally update the relay store
			// So we just fill the state with the new id
			onCompleted: ({ KeyResult_CreateEdit: response }) => {
				if (response?.keyResult) {
					newKeyResult.id = response?.keyResult.id;
				}
				const newKeyResults = [...objective.keyResults, newKeyResult];
				setObjective({
					...objective,
					keyResults: newKeyResults,
					progress: calculateObjectiveProgress(newKeyResults, objective.children),
				});
				if (alert && alertMessage === Alerts.MISSING_KEY_RESULTS) {
					setAlert(false);
				}
				setRefetchOnClose(true);
				setIsLoadingKeyResultCreation(false);
			},
		});
	}, [
		user,
		setIsLoadingKeyResultCreation,
		objective,
		setObjective,
		setAlert,
		alert,
		alertMessage,
		setRefetchOnClose,
		fromTeam,
	]);

	const onKeyResultTitleChange = useCallback(
		(newTitle: string, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						title: newTitle,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultDueDateChange = useCallback(
		(newDueDate: Moment | null, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						dueDate: newDueDate ? newDueDate.format("YYYY-MM-DD") : null,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultMetricChange = useCallback(
		(
			newMetricProgress: MetricProgress,
			newMetricType: MetricType,
			newMetricCurrency: string | undefined,
			newMetricStart: number,
			newMetricCurrent: number,
			newMetricTarget: number,
			index: number,
		) => {
			const newKeyResult = {
				...objective.keyResults[index],
				metricProgress: newMetricProgress,
				metricType: newMetricType,
				metricCurrency: newMetricCurrency ?? null,
				metricStart: newMetricStart,
				metricCurrent: newMetricCurrent,
				metricTarget: newMetricTarget,
			};
			const newKeyResults = [
				...objective.keyResults.slice(0, index),
				{
					...newKeyResult,
					progress: calculateKeyResultProgress(newKeyResult),
				},
				...objective.keyResults.slice(index + 1),
			];
			setObjective({
				...objective,
				keyResults: newKeyResults,
				progress: calculateObjectiveProgress(newKeyResults, objective.children),
			});
			setRefetchOnClose(true);
		},
		[objective, setObjective],
	);

	const onKeyResultWeightChange = useCallback(
		(newWeight: number, index: number) => {
			const newKeyResult = {
				...objective.keyResults[index],
				weight: newWeight,
			};

			const newKeyResults = [
				...objective.keyResults.slice(0, index),
				{
					...newKeyResult,
					progress: calculateKeyResultProgress(newKeyResult),
				},
				...objective.keyResults.slice(index + 1),
			];

			setObjective({
				...objective,
				keyResults: newKeyResults,
				progress: calculateObjectiveProgress(newKeyResults, objective.children),
			});
			setRefetchOnClose(true);
		},
		[objective, setObjective],
	);

	const onKeyResultOrderChange = useCallback(
		({ destination, source }: DropResult) => {
			if (!destination) {
				return;
			}

			const moveKeyResult = objective.keyResults[source.index];
			const keyResultsWithoutSource = [
				...objective.keyResults.slice(0, source.index),
				...objective.keyResults.slice(source.index + 1),
			];
			const keyResultsWithDestination = [
				...keyResultsWithoutSource.slice(0, destination.index),
				moveKeyResult,
				...keyResultsWithoutSource.slice(destination.index),
			];
			const newKeyResults = keyResultsWithDestination.map((kr, index) => ({ ...kr, order: index }));
			setObjective({ ...objective, keyResults: newKeyResults });

			if (moveKeyResult.id) {
				commitMutationPromise<KeyResultReorderMutation>(environment, {
					mutation: keyResultReorderMutation,
					variables: {
						objective: objective.id,
						oldIndex: source.index,
						newIndex: destination.index,
					},
				});
			}
		},
		[objective, setObjective],
	);

	const onKeyResultAssigneeChange = useCallback(
		(user: UserType, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						assigneeUser: user,
						assigneeTeam: null,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultAssigneeTeamChange = useCallback(
		(team: TeamType, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						assigneeTeam: team,
						assigneeUser: null,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultDelete = useCallback(
		(index: number) => {
			const keyResultId = objective.keyResults[index].id;
			const afterKeyResults = objective.keyResults.slice(index + 1).map(kr => ({ ...kr, order: kr.order - 1 }));
			const newKeyResults = [...objective.keyResults.slice(0, index), ...afterKeyResults];
			setObjective({
				...objective,
				keyResults: newKeyResults,
				progress: calculateObjectiveProgress(newKeyResults, objective.children),
			});

			const updater = (store: RecordSourceSelectorProxy<KeyResultDeleteMutation["response"]>) => {
				const node = store.get(objective.id);
				if (!node) {
					return;
				}

				const relayKeyResults = node.getLinkedRecords("keyResults");
				if (!relayKeyResults) return;

				node.setLinkedRecords(
					relayKeyResults.filter(r => r.getValue("id") !== keyResultId),
					"keyResults",
				);
			};

			if (keyResultId) {
				commitMutationPromise<KeyResultDeleteMutation>(environment, {
					mutation: keyResultDeleteMutation,
					variables: { keyResultId },
					optimisticUpdater: updater,
					updater: updater,
				});
			}
			setRefetchOnClose(true);
		},
		[objective, setObjective],
	);

	const onEditObjective = useCallback(
		(editedFields: Omit<Partial<ObjectiveInput>, "id" | "progress">) => {
			setEditIsLoading(true);
			if (!editObjective || !editObjective.id) {
				return;
			}
			if (editedFields.keyResults) {
				editedFields.keyResults = editedFields.keyResults.map(keyResult => ({
					...omit(keyResult, ["id", "progress"]),
				}));
			}
			commitMutationPromise<ObjectiveCreateOrEditMutation>(environment, {
				mutation: objectiveCreateOrEditMutation,
				variables: {
					objective: {
						id: editObjective.id,
						...editedFields,
					},
				},
				onCompleted: ({ Objective_CreateEdit: response }) => {
					setEditIsLoading(false);
					if (!response?.objective) {
						setAlert(true);
						setAlertMessage(Alerts.EDIT_ERROR);
					}
				},
			});
		},
		[editObjective],
	);

	const onChangeVisibility = useCallback(
		(newVisibility: Visibility) => {
			setObjective({ ...objective, visibility: newVisibility });
			setSettingsChanged(true);
		},
		[objective],
	);

	const onChangeCycle = useCallback(
		(newCycle: string[]) => {
			setObjective({ ...objective, cycle: { id: newCycle[0] || "" } });
			setSettingsChanged(true);
		},
		[objective],
	);

	const onChangeParent = useCallback(
		(newParent: ParentObjectiveType | null) => {
			setObjective({ ...objective, parent: newParent });
			setSettingsChanged(true);
		},
		[objective],
	);

	const onChangeContributors = useCallback(
		(assignees: AssigneeType[]) => {
			const users = assignees.map(a => a.user).filter((u): u is UserType => !!u);
			const teams = assignees.map(a => a.team).filter((t): t is TeamType => !!t);
			setObjective({ ...objective, contributors: users, contributorsTeams: teams });
			setSettingsChanged(true);
		},
		[objective],
	);

	const onDuplicateObjective = useCallback(() => {
		const keyResults = objective.keyResults.map(keyResult => {
			return { ...keyResult, id: "" };
		});
		const duplicatedObjective: ObjectiveAttributes = {
			...objective,
			keyResults,
			id: "",
			title: `(Copy) ${objective.title}`,
		};
		modals.open(({ close }) => (
			<CreateObjective onClose={close} preFilledObjective={duplicatedObjective} refetch={refetch} />
		));
		onClose();
	}, [objective, modals, onClose, refetch]);

	const onSaveSettings = useCallback(() => {
		const newContributors = [...objective.contributors.map(c => c.id), ...objective.contributorsTeams.map(t => t.id)];

		const allAssigneesSet = new Set([
			objective.assigneeUser?.id || objective.assigneeTeam?.id || "",
			...objective.keyResults.map(kr => kr.assigneeUser?.id || kr.assigneeTeam?.id || ""),
		]);

		// Validate if the contributors array contains all the objective and key result assignees.
		const isContributorsValid = Array.from(allAssigneesSet).every(id => newContributors.includes(id));

		if (!isContributorsValid) {
			setAlert(true);
			setAlertMessage(Alerts.ASSIGNEE_IS_NOT_CONTRIBUTOR);
			return;
		}

		if (alert && alertMessage === Alerts.ASSIGNEE_IS_NOT_CONTRIBUTOR) {
			setAlert(false);
		}

		onEditObjective({
			cycle: objective.cycle?.id,
			parent: objective.parent?.id || null,
			visibility: objective.visibility,
			contributors: newContributors,
		});
		if (initialSettings.cycle !== objective.cycle || initialSettings.parent !== objective.parent) {
			setRefetchOnClose(true);
		}
		setInitialSettings({
			cycle: objective.cycle,
			parent: objective.parent,
			visibility: objective.visibility,
			contributors: [...objective.contributors],
			contributorsTeams: [...objective.contributorsTeams],
		});
		setSettingsChanged(false);
	}, [
		objective,
		onEditObjective,
		initialSettings.cycle,
		initialSettings.parent,
		setInitialSettings,
		alert,
		alertMessage,
	]);

	const onChangeChildWeight = useCallback(
		(index: number, newWeight: number) => {
			if (!objective?.children || index < 0 || index >= objective.children.length) {
				return;
			}
			const newChild = {
				...objective.children[index],
				weight: newWeight,
			};

			const newChildren = [
				...objective.children.slice(0, index),
				{
					...newChild,
				},
				...objective.children.slice(index + 1),
			];
			setObjective({
				...objective,
				children: newChildren,
				// @ts-ignore
				progress: calculateObjectiveProgress(objective.keyResults, newChildren),
			});
			setRefetchOnClose(true);
		},
		[objective],
	);

	const editContextValue = useMemo(() => {
		return { editView: true, setIsLoading: setEditIsLoading };
	}, []);

	const childrenContextValue = useMemo(() => {
		return { children: true };
	}, []);

	const [isAddingDescription, setIsAddingDescription] = useState<boolean>(false);
	const [selectedOkr, setSelectedOkr] = useState<SelectorOption>({ label: objective.title, value: objective.id });

	const content = (
		<OKRsContext.Provider value={editContextValue}>
			<Objective
				{...objective}
				onAssigneeChange={onAssigneeChange}
				onAssigneeTeamChange={onAssigneeTeamChange}
				onTitleChange={onTitleChange}
				onDescriptionChange={onDescriptionChange}
				onDueDateChange={onDueDateChange}
				onStatusChange={onStatusChange}
			/>
			<div className={styles.keyResults}>
				<h3>Key results</h3>
				<EditableKeyResultList
					objective={objective}
					hasAccess={editObjective.hasAccess}
					loadingCreation={isLoadingKeyResultCreation}
					onCreate={onCreateKeyResult}
					onTitleChange={onKeyResultTitleChange}
					onDueDateChange={onKeyResultDueDateChange}
					onAssigneeChange={onKeyResultAssigneeChange}
					onAssigneeTeamChange={onKeyResultAssigneeTeamChange}
					onMetricChange={onKeyResultMetricChange}
					onWeightChange={onKeyResultWeightChange}
					onOrderChange={onKeyResultOrderChange}
					onDelete={onKeyResultDelete}
					className={cx({ [stylesCreateObjective.fieldError]: alert && alertMessage === Alerts.MISSING_KEY_RESULTS })}
					setIsAddingDescription={setIsAddingDescription}
					setSelectedOkr={setSelectedOkr}
					onChangeToUpdatesView={onChangeToUpdatesView}
				/>
			</div>

			{/* We don't need refetch the children once we have objective.children */}
			{!objective.children && (
				<RunQuery<EditObjectiveChildrenQuery>
					query={childItemsQuery}
					variables={{ parent: objective.id }}
					// Ideally we would use store-and-network here
					// But on Relay 12 the useLazyLoadQuery hook is not returning
					// The updated value once the request from the network returns.
					// While Relay doesn't fix that we will have to always request it from
					// the network
					fetchPolicy="network-only"
					onLoading={onChildQueryLoading}
					onResult={onChildQueryResponse}
				/>
			)}

			{objective.children && objective.children.length > 0 && (
				<div className={styles.children}>
					<h3>Children objectives</h3>
					<OKRsContext.Provider value={childrenContextValue}>
						{objective.children.map((childObjective, index) => (
							<Objective
								key={`child_${childObjective.id}`}
								{...childObjective}
								hasAccess={objective.hasAccess && childObjective.hasAccess}
								/* eslint-disable-next-line react/jsx-no-bind */
								onWeightChange={(newWeight: number) => onChangeChildWeight(index, newWeight)}
							/>
						))}
					</OKRsContext.Provider>
				</div>
			)}
		</OKRsContext.Provider>
	);

	const menu = (
		<Popup
			open={optionsOpen}
			className={styles.optionsPopup}
			position="bottom right"
			trigger={
				<IconButton
					title="Options"
					icon={faEllipsisV}
					className={cx("kebabMenu", styles.kebabMenu)}
					onClick={onOpenOptions}
				/>
			}
		>
			<OutsideClickHandler onOutsideClick={onCloseOptions}>
				<div className={styles.options}>
					<Button size="wide" variant="tertiary" onClick={onCopyObjectiveURL}>
						<FontAwesomeIcon fixedWidth icon={faLink} />
						Copy Objective URL
					</Button>
					{objective.hasAccess && (
						<>
							<hr />
							<Button size="wide" variant="tertiary" onClick={onArchiveObjective}>
								<FontAwesomeIcon fixedWidth icon={faArchive} />
								{objective.archived ? "Unarchive Objective..." : "Archive Objective..."}
							</Button>
							<Button size="wide" variant="tertiary" onClick={onDuplicateObjective}>
								<FontAwesomeIcon fixedWidth icon={faCopy} />
								Duplicate Objective
							</Button>
							{isEditLoading || isChildQueryLoading || (objective.children && objective.children.length > 0) ? (
								<Popup
									position="top center"
									inverted
									trigger={
										<Button size="wide" variant="tertiary" className={styles.destructiveOption} disabled>
											<FontAwesomeIcon fixedWidth icon={faTrashCan} />
											Delete Objective...
										</Button>
									}
									size="tiny"
									content="You cannot delete parent Objectives"
								/>
							) : (
								<Button size="wide" variant="tertiary" onClick={onDeleteObjective} className={styles.destructiveOption}>
									<FontAwesomeIcon fixedWidth icon={faTrashCan} />
									Delete Objective...
								</Button>
							)}
						</>
					)}
				</div>
			</OutsideClickHandler>
		</Popup>
	);

	return (
		<Modal
			className={cx(styles.modal, stylesCreateObjective.modal, { [styles.modalWithParent]: Boolean(objective.parent) })}
			onClose={onClose}
			data-test-id="edit-objective-modal"
			lockIgnoredClasses={["Input", "TextField"]}
		>
			{!isMobile && (
				<section className={styles.content}>
					<Modal.Header
						className={cx(styles.header, stylesCreateObjective.header, {
							[styles.headerWithParent]: Boolean(objective.parent),
						})}
					>
						{objective.parent && (
							<a
								className={cx(styles.parentHeader, { [styles.untitled]: !objective.parent.title })}
								href={`${window.location.origin}/objectives/${objective.parent.id}/`}
								target="_blank"
								rel="noopener"
							>
								<FontAwesomeIcon icon={faBullseyeArrow} />
								{objective.parent.title || "Untitled objective"} /
							</a>
						)}
						<FontAwesomeIcon icon={faBullseyeArrow} />
						<Modal.Title>{objective.title.trim() || <i>Untitled objective</i>}</Modal.Title>
						<span className={styles.savingMessage}>
							{isEditLoading ? (
								<>
									Saving <LoadingSpinner inline />
								</>
							) : isEditLoading === false ? (
								<>
									Saved <FontAwesomeIcon fixedWidth icon={faCheck} />
								</>
							) : null}
						</span>
						{menu}
					</Modal.Header>
					{alert && alertMessage && (
						<Alert error={ErrorAlerts.includes(alertMessage)} icon={AlertIcons[alertMessage]}>
							{alertMessage}
						</Alert>
					)}
					<Modal.Body className={cx(styles.body, stylesCreateObjective.body)}>{content}</Modal.Body>
				</section>
			)}
			<section className={styles.tabsSection}>
				<Modal.Header
					className={cx(styles.header, stylesCreateObjective.header, {
						[styles.headerWithParent]: Boolean(objective.parent),
					})}
				>
					<Modal.HeaderActions onClose={onClose} className={styles.headerWithTabs}>
						<div className={styles.tabs}>
							{isMobile && (
								<Button
									size="wide"
									className={cx(styles.tab, { [styles.activeTab]: view === Viewing.OVERVIEW })}
									onClick={onChangeToOverviewView}
								>
									Overview
								</Button>
							)}
							<Button
								size="wide"
								className={cx(styles.tab, { [styles.activeTab]: view === Viewing.UPDATES })}
								onClick={onChangeToUpdatesView}
							>
								Activity
							</Button>
							<Button
								size="wide"
								className={cx(styles.tab, { [styles.activeTab]: view === Viewing.SETTINGS })}
								onClick={onChangeToSettingsView}
							>
								Settings
							</Button>
						</div>
						<div>
							{isMobile && menu}
							{!isMobile && <HelpCenterIcon articleId="5607359" title="More information..." />}
						</div>
					</Modal.HeaderActions>
				</Modal.Header>
				<Modal.Body>
					{view === Viewing.OVERVIEW && content}
					{view === Viewing.UPDATES && (
						<React.Suspense fallback={<LoadingSpinner inline />}>
							<Updates
								objectiveId={objective.id}
								objectiveTitle={objective.title}
								keyResults={objective.keyResults}
								contributors={objective.contributors}
								hasAccess={editObjective.hasAccess}
								isAddingDescription={isAddingDescription}
								setIsAddingDescription={setIsAddingDescription}
								selectedOkr={selectedOkr}
								setSelectedOkr={setSelectedOkr}
							/>
						</React.Suspense>
					)}
					{view === Viewing.SETTINGS && (
						<Settings
							objective={objective}
							hasAccess={editObjective.hasAccess}
							columnLayout
							allowSubmit={settingsChanged && !(isEditLoading || isChildQueryLoading)}
							className={styles.settings}
							onChangeCycles={onChangeCycle}
							onChangeParent={onChangeParent}
							onChangeVisibility={onChangeVisibility}
							onChangeContributors={onChangeContributors}
							onSubmit={onSaveSettings}
						/>
					)}
				</Modal.Body>
			</section>
		</Modal>
	);
}
